import { useEffect, useRef, useState } from "react";
import { fabric } from 'fabric';
import { CropOverlay } from "./CropOverlay";
import type { Image as FabricImage, Canvas as FabricCanvas, IText } from 'fabric/fabric-impl';

interface CanvasProps {
  companySlug: string;
  screenshotUrl: string;
  activeTool: "select" | "draw" | "rectangle" | "move" | "text" | "crop";
  onCanvasInitialized: (canvas: fabric.Canvas) => void;
  initialFabricJson?: any;
  onCanvasChange?: (canvas: fabric.Canvas) => void;
  isEditMode?: boolean;
}

export const Canvas = ({
  companySlug,
  screenshotUrl,
  activeTool,
  onCanvasInitialized,
  initialFabricJson,
  onCanvasChange,
  isEditMode = false
}: CanvasProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<FabricCanvas | null>(null);
  const backgroundImageRef = useRef<FabricImage | null>(null);
  const isInitializedRef = useRef(false);
  const [isDrawingRect, setIsDrawingRect] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<fabric.Rect | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPointer, setLastPointer] = useState<{ x: number; y: number } | null>(null);
  const [cropRect, setCropRect] = useState<fabric.Rect | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  // Add zoom functionality to window object
  useEffect(() => {
    if (!canvasRef.current) return;

    const handleZoom = (delta: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const newZoom = Math.min(Math.max(zoomLevel + delta, 0.1), 5);

      // Get the center point of the viewport
      const center = {
        x: canvas.width! / 2,
        y: canvas.height! / 2
      };

      // Set the zoom point to the center of the viewport
      canvas.zoomToPoint({ x: center.x, y: center.y }, newZoom);

      setZoomLevel(newZoom);

      if (onCanvasChange) {
        onCanvasChange(canvas);
      }
    };

    // Attach the zoom handler to the window object
    (window as any)[`handleCanvasZoom_${companySlug}`] = handleZoom;

    return () => {
      delete (window as any)[`handleCanvasZoom_${companySlug}`];
    };
  }, [companySlug, zoomLevel, onCanvasChange]);

  // Add keyboard event handler for deletion
  useEffect(() => {
    if (!canvasRef.current || !isEditMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const canvas = canvasRef.current;
        if (!canvas || activeTool !== 'select') return;

        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject !== backgroundImageRef.current) {
          canvas.remove(activeObject);
          canvas.renderAll();
          if (onCanvasChange) onCanvasChange(canvas);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, activeTool, onCanvasChange]);

  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;
    isInitializedRef.current = true;

    // console.log('🎨 [Canvas] Initializing canvas for:', companySlug);
    const canvas = new fabric.Canvas(`canvas-${companySlug}`, {
      width: containerRef.current.offsetWidth,
      height: containerRef.current.offsetHeight,
    });

    canvasRef.current = canvas;
    onCanvasInitialized(canvas);

    // Set crossOrigin when loading the image
    fabric.Image.fromURL(screenshotUrl, (img) => {
      // console.log('🖼️ [Canvas] Loading background image for:', companySlug);
      if (img instanceof fabric.Image) {
        img.scaleToWidth(canvas.width!);
        backgroundImageRef.current = img;
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));

        if (initialFabricJson) {
          canvas.loadFromJSON(initialFabricJson, () => {
            canvas.renderAll();
          });
        }
      }
    }, { crossOrigin: 'anonymous' });

    return () => {
      // console.log('👋 [Canvas] Cleaning up canvas for:', companySlug);
      canvas.dispose();
      isInitializedRef.current = false;
    };
  }, [companySlug, screenshotUrl, onCanvasInitialized, initialFabricJson]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;

    const handleMouseDown = (e: fabric.IEvent) => {
      if (!isEditMode) return;

      if (activeTool === "move") {
        setIsDragging(true);
        const pointer = canvas.getPointer(e.e);
        setLastPointer({ x: pointer.x, y: pointer.y });
        canvas.selection = false;
      } else if (activeTool === "rectangle") {
        const pointer = canvas.getPointer(e.e);
        setIsDrawingRect(true);
        setStartPoint({ x: pointer.x, y: pointer.y });

        const rect = new fabric.Rect({
          left: pointer.x,
          top: pointer.y,
          width: 0,
          height: 0,
          fill: 'transparent',
          stroke: 'red',
          strokeWidth: 2,
          selectable: true,
          evented: true
        });

        canvas.add(rect);
        setCurrentRect(rect);
        canvas.renderAll();
      } else if (activeTool === "text") {
        const pointer = canvas.getPointer(e.e);
        const text = new fabric.IText("", {
          left: pointer.x,
          top: pointer.y,
          fontSize: 20,
          fill: 'red',
          fontFamily: 'Roboto, sans-serif',
          selectable: true,
          evented: true,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          padding: 5
        });

        canvas.add(text);
        canvas.setActiveObject(text);

        requestAnimationFrame(() => {
          if (text instanceof fabric.IText) {
            text.enterEditing();
            text.hiddenTextarea?.focus();
            canvas.requestRenderAll();
          }
        });

        if (onCanvasChange) onCanvasChange(canvas);
        window.dispatchEvent(new CustomEvent('switchToSelectTool'));
      } else if (activeTool === "crop" && !isCropping) {
        const pointer = canvas.getPointer(e.e);
        setStartPoint({ x: pointer.x, y: pointer.y });

        const rect = new fabric.Rect({
          left: pointer.x,
          top: pointer.y,
          width: 0,
          height: 0,
          fill: 'rgba(0, 0, 0, 0.3)',
          stroke: 'white',
          strokeWidth: 2,
          selectable: true,
          evented: true,
          cornerColor: 'white',
          cornerSize: 10,
          transparentCorners: false
        });

        canvas.add(rect);
        setCropRect(rect);
        setIsCropping(true);
        canvas.renderAll();
      }
    };

    const handleMouseMove = (e: fabric.IEvent) => {
      if (!isEditMode) return;

      if (activeTool === "move" && isDragging && lastPointer) {
        const pointer = canvas.getPointer(e.e);
        const deltaX = pointer.x - lastPointer.x;
        const deltaY = pointer.y - lastPointer.y;

        canvas.getObjects().forEach((obj) => {
          if (obj.left !== undefined && obj.top !== undefined) {
            obj.set({
              left: obj.left + deltaX,
              top: obj.top + deltaY
            });
          }
        });

        const bg = canvas.backgroundImage as fabric.Image;
        if (bg && bg.left !== undefined && bg.top !== undefined) {
          bg.set({
            left: bg.left + deltaX,
            top: bg.top + deltaY
          });
        }

        setLastPointer({ x: pointer.x, y: pointer.y });
        canvas.renderAll();
      } else if ((isDrawingRect || (activeTool === "crop" && isCropping && startPoint)) && startPoint) {
        const pointer = canvas.getPointer(e.e);
        const width = pointer.x - startPoint.x;
        const height = pointer.y - startPoint.y;
        const rect = activeTool === "crop" ? cropRect : currentRect;

        if (rect) {
          rect.set({
            width: Math.abs(width),
            height: Math.abs(height),
            left: width > 0 ? startPoint.x : pointer.x,
            top: height > 0 ? startPoint.y : pointer.y,
          });

          canvas.renderAll();
        }
      }
    };

    const handleMouseUp = () => {
      if (!isEditMode) return;

      if (activeTool === "move") {
        setIsDragging(false);
        setLastPointer(null);
        if (onCanvasChange) onCanvasChange(canvas);
      } else if (isDrawingRect) {
        setIsDrawingRect(false);
        setStartPoint(null);
        if (onCanvasChange) onCanvasChange(canvas);
      }

      // Make sure to stop any ongoing drawing operations
      if (activeTool === "crop" && startPoint) {
        setStartPoint(null);
        if (cropRect) {
          cropRect.setCoords();
          canvas.setActiveObject(cropRect);
        }
      }
    };

    if (activeTool === "draw") {
      canvas.isDrawingMode = isEditMode;
      canvas.defaultCursor = isEditMode ? 'crosshair' : 'default';
      canvas.selection = false;
      canvas.freeDrawingBrush.width = 2;
      canvas.freeDrawingBrush.color = '#ff0000';
    } else if (activeTool === "rectangle" || activeTool === "crop") {
      canvas.isDrawingMode = false;
      canvas.defaultCursor = isEditMode ? 'crosshair' : 'default';
      canvas.selection = false;
    } else if (activeTool === "move") {
      canvas.isDrawingMode = false;
      canvas.defaultCursor = isEditMode ? 'move' : 'default';
      canvas.selection = false;
    } else if (activeTool === "text") {
      canvas.isDrawingMode = false;
      canvas.defaultCursor = isEditMode ? 'text' : 'default';
      canvas.selection = true;
    } else {
      canvas.isDrawingMode = false;
      canvas.defaultCursor = 'default';
      canvas.selection = isEditMode;
    }

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [activeTool, isDrawingRect, startPoint, currentRect, isDragging, lastPointer, onCanvasChange, isEditMode, isCropping, cropRect]);

  const handleApplyCrop = () => {
    if (!canvasRef.current || !cropRect || !backgroundImageRef.current) return;

    const canvas = canvasRef.current;
    const bg = backgroundImageRef.current;

    const bgElement = bg.getElement() as HTMLImageElement;
    if (!bgElement) return;

    // Calculate the scale of the background image
    const bgWidth = bgElement.naturalWidth;
    const bgHeight = bgElement.naturalHeight;
    const scaleX = bg.scaleX || 1;
    const scaleY = bg.scaleY || 1;

    // Get the crop rectangle dimensions in canvas coordinates
    const { left = 0, top = 0, width = 0, height = 0 } = cropRect;

    // Calculate the crop dimensions relative to the original image
    const cropLeft = (left - (bg.left || 0)) / scaleX;
    const cropTop = (top - (bg.top || 0)) / scaleY;
    const cropWidth = width / scaleX;
    const cropHeight = height / scaleY;

    // Create a temporary canvas for cropping
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cropWidth;
    tempCanvas.height = cropHeight;
    const ctx = tempCanvas.getContext('2d');

    if (!ctx) return;

    // Ensure crossOrigin is set
    bgElement.crossOrigin = 'anonymous';

    // Draw the cropped portion to the temporary canvas
    try {
      ctx.drawImage(
        bgElement,
        cropLeft, cropTop, cropWidth, cropHeight,  // Source rectangle
        0, 0, cropWidth, cropHeight                // Destination rectangle
      );

      // Convert the cropped canvas to a data URL
      const croppedDataUrl = tempCanvas.toDataURL();

      // Create a new fabric.Image with the cropped data
      fabric.Image.fromURL(croppedDataUrl, (img) => {
        if (img instanceof fabric.Image) {
          // Calculate container dimensions
          const containerWidth = canvas.width!;
          const containerHeight = canvas.height!;

          // Calculate scaling to fit the container while maintaining aspect ratio
          const scaleX = containerWidth / cropWidth;
          const scaleY = containerHeight / cropHeight;
          const scale = Math.min(scaleX, scaleY);

          // Scale the image to fit within the container
          img.scale(scale);

          // Calculate centered position
          const scaledWidth = cropWidth * scale;
          const scaledHeight = cropHeight * scale;
          const left = (containerWidth - scaledWidth) / 2;
          const top = (containerHeight - scaledHeight) / 2;

          // Position the image
          img.set({
            left,
            top,
            originX: 'left',
            originY: 'top'
          });

          // Clear the canvas and set the new background image
          canvas.clear();
          canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
          backgroundImageRef.current = img;

          // Reset crop-related states
          setCropRect(null);
          setIsCropping(false);
          setZoomLevel(1); // Reset zoom level after crop

          // Notify about canvas changes if needed
          if (onCanvasChange) onCanvasChange(canvas);
        }
      }, { crossOrigin: 'anonymous' });
    } catch (error) {
      console.error('Error during crop operation:', error);
    }
  };

  const handleCancelCrop = () => {
    if (!canvasRef.current || !cropRect) return;

    canvasRef.current.remove(cropRect);
    setCropRect(null);
    setIsCropping(false);
    canvasRef.current.renderAll();
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden"
    >
      <canvas id={`canvas-${companySlug}`} />
      {isCropping && (
        <CropOverlay
          onApply={handleApplyCrop}
          onCancel={handleCancelCrop}
        />
      )}
    </div>
  );
};
